/**
 * Task View Configurations
 *
 * Defines different views for task-oriented inventory management.
 * Each view has focused columns and contextual status rules.
 */

import { InventoryPlanningRow, SortDirection } from "../types/inventory-planning";
import { InventoryStatus } from "../types/product-context";

// ==================== View Types ====================

type TaskViewType = "status" | "product_alerts";

interface TaskViewConfig {
  /** View identifier */
  id: TaskViewType;

  /** Display name */
  name: string;

  /** Short description */
  description: string;

  /** Mental model / user question this view answers */
  mentalModel: string;

  /** Route path */
  path: string;

  /** Column IDs to show by default */
  defaultColumns: (keyof InventoryPlanningRow)[];

  /** Default sort column */
  defaultSortColumn: keyof InventoryPlanningRow;

  /** Default sort direction */
  defaultSortDirection: SortDirection;

  /** Pre-applied status filters (null = show all) */
  statusFilters: InventoryStatus[] | null;

  /** Whether to show status filter pills */
  showStatusPills: boolean;

  /** Icon name (heroicons) */
  icon: "exclamation-triangle" | "squares-2x2";
}

// ==================== View Configurations ====================

export const TASK_VIEWS: Record<TaskViewType, TaskViewConfig> = {
  status: {
    id: "status",
    name: "Status",
    description: "Status summary with pending resolution section",
    mentalModel: "What needs attention and what's pending resolution?",
    path: "/inventory/status",
    defaultColumns: ["productName", "daysLeft30", "kcInventory", "amazonInventory"],
    defaultSortColumn: "daysLeft30",
    defaultSortDirection: "asc",
    statusFilters: null,
    showStatusPills: true,
    icon: "squares-2x2",
  },

  product_alerts: {
    id: "product_alerts",
    name: "Alerts",
    description: "Items needing immediate attention with product+platform view",
    mentalModel: "What needs immediate attention across all platforms?",
    path: "/inventory/alerts",
    defaultColumns: ["productName", "daysLeft30", "kcInventory", "amazonInventory", "kcIncoming"],
    defaultSortColumn: "daysLeft30",
    defaultSortDirection: "asc",
    statusFilters: ["urgent", "at_risk"],
    showStatusPills: true,
    icon: "exclamation-triangle",
  },
};

// ==================== Navigation Config ====================

/**
 * Navigation items for Inventory Planning dropdown menu.
 */
export const INVENTORY_NAV_ITEMS: Array<{ name: string; path: string }> = [
  { name: "Metrics", path: "/inventory/metrics" },
  { name: "Rebalance planning", path: "/inventory/fba-replenishment" },
  TASK_VIEWS.product_alerts,
  TASK_VIEWS.status,
  { name: "Kitting", path: "/inventory/kitting" },
  { name: "Forecasts", path: "/inventory/forecasts" },
];

// ==================== Route Helpers ====================

/**
 * Check if a path is under inventory routes.
 */
export function isInventoryPath(path: string): boolean {
  return path.startsWith("/inventory");
}
